import { Link, useLocation } from "wouter";
import { useClerk } from "@clerk/react";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";
import { useEffect } from "react";
import {
  LayoutDashboard,
  Microscope,
  Activity,
  ClipboardList,
  Pill,
  MapPin,
  MessageSquare,
  LogOut,
  Settings,
  GraduationCap,
  ShieldCheck,
  BookOpen,
  Briefcase,
  Star,
  FileSearch,
  AlertTriangle,
  Users,
  Mic,
  CalendarDays,
} from "lucide-react";
import { LastSignInBanner } from "@/components/ui/last-signin-banner";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { SessionNoticeBanner } from "@/components/ui/session-notice-banner";
import { HcpVerificationBanner } from "@/components/ui/hcp-verification-banner";
import { LegalFooter } from "@/components/layout/legal-footer";
import { GlobalSearch } from "@/components/ui/global-search";
import { DashboardHeader } from "@/components/layout/dashboard-header";

export function DoctorLayout({ children, title }: { children: React.ReactNode; title: string }) {
  const [location] = useLocation();
  const { signOut } = useClerk();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    document.title = `${title} | RheumHub`;
    return () => { document.title = "RheumHub"; };
  }, [title]);

  const clinicalItems = [
    { href: "/doctor", label: "Dashboard", icon: LayoutDashboard },
    { href: "/doctor/diagnostic-criteria", label: "Diagnostic Criteria", icon: Microscope },
    { href: "/doctor/disease-activity", label: "Disease Activity Scores", icon: Activity },
    { href: "/doctor/prescribing", label: "Prescribing", icon: Pill },
    { href: "/doctor/smpc", label: "SmPC Reference", icon: FileSearch },
  ];

  const hubItems = [
    { href: "/doctor/gp-referral", label: "GP Referral Hub", icon: MapPin },
  ];

  const assistantItems = [
    { href: "/doctor/chat", label: "AI Clinical Assistant", icon: MessageSquare },
    { href: "/doctor/dictation", label: "Note Dictation", icon: Mic },
  ];

  const cpdItems = [
    { href: "/doctor/learn", label: "Learning Hub", icon: BookOpen },
    { href: "/doctor/cpd", label: "CPD Activity Log", icon: GraduationCap },
    { href: "/doctor/cpd/revalidation", label: "Revalidation Dashboard", icon: ShieldCheck },
    { href: "/doctor/cpd/curriculum", label: "JRCPTB Curriculum", icon: BookOpen },
  ];

  const careerItems = [
    { href: "/doctor/career", label: "Career Pathways", icon: Briefcase },
    { href: "/doctor/career/interview-prep", label: "Interview Prep", icon: Star },
  ];

  const eventsItems = [
    { href: "/doctor/events", label: "Events", icon: CalendarDays },
  ];

  const governanceItems = [
    { href: "/hcp/safety-report", label: "Report Safety Concern", icon: AlertTriangle },
    { href: "/doctor/settings", label: "Your Data", icon: Settings },
  ];

  const isActive = (href: string) =>
    location === href || (location.startsWith(href + "/") && href !== "/doctor") || (location === href && href !== "/doctor");

  const isActiveExact = (href: string) => location === href;

  const renderNavItem = (item: { href: string; label: string; icon: React.ElementType }, exact = false) => {
    const active = exact ? isActiveExact(item.href) : isActive(item.href);
    return (
      <Link
        key={item.href}
        href={item.href}
        className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
          active
            ? "bg-[#f9b0be]/15 text-[#f9b0be]"
            : "hover:bg-white/10 hover:text-white"
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <item.icon className={`h-4 w-4 shrink-0 ${active ? "text-[#f9b0be]" : "text-white/35"}`} />
        {item.label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <SessionNoticeBanner />
      <LastSignInBanner />
      <HcpVerificationBanner />
      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-64 bg-[#4a0816] text-white/75 flex flex-col fixed inset-y-0 z-20">
          <div className="p-6 flex items-center gap-3 text-white">
            <Activity className="h-6 w-6 text-[#f9b0be]" />
            <span className="text-xl font-bold tracking-tight">RheumHub</span>
          </div>

          <div className="px-6 py-2 text-xs font-semibold uppercase tracking-wider text-[#f9b0be]/60">
            Doctor Portal
          </div>

          <nav className="sidebar-nav flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
            <div className="pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Clinical Tools
              </div>
            </div>
            {clinicalItems.map((item) => renderNavItem(item, item.href === "/doctor"))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                GP Referral Hub
              </div>
            </div>
            {hubItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                AI Assistant
              </div>
            </div>
            {assistantItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                CPD Portfolio
              </div>
            </div>
            {cpdItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Career
              </div>
            </div>
            {careerItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Events
              </div>
            </div>
            {eventsItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Governance
              </div>
            </div>
            {governanceItems.map((item) => renderNavItem(item))}
          </nav>

          <div className="p-3 border-t border-white/10 space-y-0.5">
            {isAdmin && (
              <Link
                href="/admin"
                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                  location.startsWith("/admin")
                    ? "bg-[#7bb8ff]/15 text-[#7bb8ff]"
                    : "hover:bg-white/10 hover:text-white text-white/35"
                }`}
              >
                <Users className="h-4 w-4 shrink-0" />
                Admin Panel
              </Link>
            )}
            <button
              onClick={() => { clearRheumHubLocalStorage(); signOut({ redirectUrl: "/" }); }}
              className="flex w-full items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium hover:bg-white/10 transition-colors text-white/35 hover:text-white"
              data-testid="nav-signout"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 pl-64 flex flex-col min-h-screen">
          <DashboardHeader title={title} profileLink="/doctor/profile" />
          <div className="flex-1 p-8">
            <div className="max-w-6xl mx-auto">
              {children}
            </div>
          </div>
          <LegalFooter
            copyright="© RheumHub · For UK rheumatology doctors only · AI responses do not constitute clinical advice · Grounded in GMC, NICE, BSR, EULAR"
            extraLinks={[{ label: "Clinical Safety", href: "/clinical-safety" }]}
          />
        </main>
      </div>
    </div>
  );
}
